import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Copy, Keyboard, IndianRupee, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SmartIngredientInput from './SmartIngredientInput';
import { toCanonical, normalizeUnitKey, ACCEPTED_UNITS_FOR_UI } from '../lib/units';
import { createRecipe, updateRecipe } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import logger from '../lib/logger';

/**
 * RecipeEditorDialog — operator-grade recipe entry/edit, mirroring PrepEditorDialog.
 *
 * Modes:
 *   mode='create' → POST /api/inventory/recipes
 *   mode='edit'   → PUT  /api/inventory/recipes/{recipe_id}   (RBAC enforced server-side)
 *
 * Keyboard:
 *   - Enter on an ingredient input  : advance to qty
 *   - Enter on qty                  : advance to next row (creates row if at end)
 *   - Tab / Shift+Tab               : native cell-to-cell
 *   - Cmd/Ctrl + Enter              : Save
 *   - Cmd/Ctrl + D on a row's qty   : Duplicate that row
 *   - Cmd/Ctrl + Delete on qty      : Remove that row
 */

const blankLine = () => ({
  ingredient_id: '',
  qty: '',
  base_unit: 'gm',
  wastage_percent: '',
  _key: Math.random().toString(36).slice(2),
});

// Yield units include operator-friendly portion / serving in addition to canonical
const YIELD_UNIT_OPTIONS = ['portion', 'serving', ...ACCEPTED_UNITS_FOR_UI];

const RecipeEditorDialog = ({
  open,
  onClose,
  mode = 'create',
  existingRecipe = null,
  ingredients = [],
  outletId = null,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [recipeType, setRecipeType] = useState('MENU_ITEM');
  const [category, setCategory] = useState('');
  const [yieldQty, setYieldQty] = useState('1');
  const [yieldUnit, setYieldUnit] = useState('portion');
  const [lines, setLines] = useState([blankLine()]);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Hydrate when opened
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && existingRecipe) {
      setName(existingRecipe.recipe_name || '');
      setRecipeType(existingRecipe.recipe_type || 'MENU_ITEM');
      setCategory(existingRecipe.category || '');
      setYieldQty(String(existingRecipe.yields?.yield_qty ?? 1));
      setYieldUnit(existingRecipe.yields?.yield_unit || 'portion');
      const next = (existingRecipe.lines || [])
        .filter((l) => (l.line_type || 'INGREDIENT') === 'INGREDIENT')
        .map((l) => ({
          ingredient_id: l.ingredient_id || '',
          qty: String(l.qty ?? ''),
          base_unit: l.base_unit || 'gm',
          wastage_percent: l.wastage_percent != null ? String(l.wastage_percent) : '',
          _key: Math.random().toString(36).slice(2),
        }));
      setLines(next.length ? next : [blankLine()]);
      setNotes(existingRecipe.notes || '');
      setReason('');
    } else {
      setName(''); setRecipeType('MENU_ITEM'); setCategory('');
      setYieldQty('1'); setYieldUnit('portion');
      setLines([blankLine()]); setNotes(''); setReason('');
    }
  }, [open, mode, existingRecipe]);

  // ── ingredient lookup ─────────────────────────────────────────
  const ingredientById = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => m.set(i.ingredient_id, i));
    return m;
  }, [ingredients]);

  // ── live cost preview per row using effective_price ──────────
  const lineCosts = lines.map((line) => {
    if (!line.ingredient_id || !line.qty) return { cost: 0, missing: true, mismatch: false };
    const ing = ingredientById.get(line.ingredient_id);
    if (!ing) return { cost: 0, missing: true, mismatch: false };
    const price = Number(ing.effective_price || 0);
    const ingUnit = ing.base_uom || ing.ingredient_base_unit || '';
    if (!price) return { cost: 0, missing: true, mismatch: false };

    const lineCanon = toCanonical(Number(line.qty || 0), line.base_unit);
    const ingCanon = toCanonical(1, ingUnit);
    if (lineCanon.conversion_failed || ingCanon.conversion_failed) return { cost: 0, missing: true, mismatch: false };
    if (lineCanon.base_unit !== ingCanon.base_unit) return { cost: 0, missing: false, mismatch: true };
    const priceCanon = price / (ingCanon.factor || 1);
    const wastage = Math.max(0, Number(line.wastage_percent || 0)) / 100;
    return { cost: lineCanon.qty * priceCanon * (1 + wastage), missing: false, mismatch: false };
  });

  const totalCost = lineCosts.reduce((s, c) => s + (c.cost || 0), 0);
  const yieldQtyNum = Number(yieldQty || 0);
  const yieldIsCanon = !!toCanonical(1, yieldUnit) && !toCanonical(1, yieldUnit).conversion_failed;
  const yieldIsPassthrough = ['portion', 'serving', 'plate'].includes((yieldUnit || '').toLowerCase());
  const yieldUnitInvalid = !yieldIsCanon && !yieldIsPassthrough;
  const costPerYield = yieldQtyNum > 0 ? totalCost / yieldQtyNum : 0;

  const missingCount = lineCosts.filter((c, idx) => c.missing && lines[idx]?.ingredient_id).length;
  const mismatchCount = lineCosts.filter((c) => c.mismatch).length;
  const invalidUnits = lines.filter((l) => l.base_unit && toCanonical(1, l.base_unit).conversion_failed).length;
  const hasUnresolvedRows = lines.some((l) => l.qty && !l.ingredient_id);

  // ── handlers ─────────────────────────────────────────────────
  const updateLine = (idx, patch) => setLines((r) => r.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines((r) => [...r, blankLine()]);
  const addFiveLines = () => setLines((r) => [...r, ...Array.from({ length: 5 }, () => blankLine())]);
  const dupLine = (idx) => setLines((r) => {
    const copy = [...r];
    copy.splice(idx + 1, 0, { ...r[idx], _key: Math.random().toString(36).slice(2) });
    return copy;
  });
  const removeLine = (idx) => setLines((r) => r.length === 1 ? r : r.filter((_, i) => i !== idx));

  const qtyRefs = useRef([]);
  const focusNextQty = (idx) => {
    const next = qtyRefs.current[idx + 1];
    if (next) next.focus();
    else { addLine(); setTimeout(() => qtyRefs.current[idx + 1]?.focus(), 50); }
  };

  const onGlobalKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onGlobalKeyDown);
    return () => document.removeEventListener('keydown', onGlobalKeyDown);
  }, [open, name, yieldQty, yieldUnit, lines, reason, notes, recipeType, category]); // eslint-disable-line

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Recipe name is required'); return; }
    const yq = Number(yieldQty);
    if (!yq || yq <= 0) { toast.error('Yield quantity must be greater than 0'); return; }
    if (yieldUnitInvalid) { toast.error(`Yield unit '${yieldUnit}' is not recognized`); return; }

    const valid = lines.filter((l) => l.ingredient_id && Number(l.qty) > 0);
    if (valid.length === 0) { toast.error('At least one resolved recipe line is required'); return; }
    if (hasUnresolvedRows) { toast.error('One or more rows have unresolved ingredients. Please select from the master list.'); return; }
    if (invalidUnits > 0) { toast.error('One or more lines have unrecognized units'); return; }

    setSaving(true);
    try {
      const payload = {
        recipe_name: name.trim(),
        recipe_type: recipeType,
        category: category.trim() || null,
        yields: { yield_qty: yq, yield_unit: yieldIsPassthrough ? yieldUnit.toLowerCase() : normalizeUnitKey(yieldUnit) },
        lines: valid.map((l) => ({
          line_type: 'INGREDIENT',
          ingredient_id: l.ingredient_id,
          qty: Number(l.qty),
          base_unit: normalizeUnitKey(l.base_unit),
          wastage_percent: l.wastage_percent === '' ? 0 : Number(l.wastage_percent),
        })),
        notes: notes || null,
      };

      if (mode === 'edit' && existingRecipe) {
        if (reason.trim()) payload.reason = reason.trim();
        await updateRecipe(existingRecipe.recipe_id, payload);
        toast.success('Recipe updated');
      } else {
        if (outletId) payload.outlet_id = outletId;
        await createRecipe(payload);
        toast.success('Recipe created');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      logger.error('save recipe failed', e);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save recipe'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="recipe-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            {mode === 'edit' ? 'Edit Recipe' : 'Create New Recipe'}
            <span className="ml-auto text-xs font-normal text-zinc-500 flex items-center gap-1">
              <Keyboard className="w-3 h-3" /> Cmd/Ctrl+Enter to save · Cmd/Ctrl+D to duplicate row
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Operator-grade recipe editor. Resolve every ingredient via the smart input,
            set quantity in canonical units, and save with Cmd/Ctrl+Enter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Header: name, type, category, yield */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-5">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Recipe Name *</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Margherita Pizza" autoFocus={mode === 'create'}
                data-testid="recipe-editor-name"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Type *</label>
              <select
                value={recipeType} onChange={(e) => setRecipeType(e.target.value)}
                data-testid="recipe-editor-type"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              >
                <option value="MENU_ITEM">Menu Item</option>
                <option value="PREP">Prep</option>
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Category <span className="text-zinc-500 font-normal">(optional)</span></label>
              <input
                type="text" value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Pizza, Beverage"
                data-testid="recipe-editor-category"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
              <p className="mt-1 text-[10px] text-zinc-500">Leave blank to inherit category from the mapped POS / Sales item.</p>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Yield Qty *</label>
              <input
                type="number" step="0.01" min="0.01" value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)} placeholder="1"
                data-testid="recipe-editor-yield-qty"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-zinc-400 mb-1">Yield Unit *</label>
              <select
                value={yieldUnit} onChange={(e) => setYieldUnit(e.target.value)}
                data-testid="recipe-editor-yield-unit"
                className={`w-full px-3 py-2 bg-[#09090b] border rounded-md text-sm outline-none ${yieldUnitInvalid ? 'border-red-500/60' : 'border-[#3f3f46] focus:border-emerald-500'}`}
              >
                {YIELD_UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          {/* Recipe table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Recipe Lines *</label>
              <div className="flex gap-2">
                <button type="button" onClick={addLine} data-testid="recipe-editor-add-row" className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46] flex items-center gap-1"><Plus className="w-3 h-3" /> Add row</button>
                <button type="button" onClick={addFiveLines} data-testid="recipe-editor-add-5-rows" className="text-xs px-2 py-1 rounded bg-[#27272a] hover:bg-[#3f3f46]">+5 rows</button>
              </div>
            </div>

            <div className="border border-[#27272a] rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-0 px-2 py-2 bg-[#0c0c0e] text-xs text-zinc-500 font-medium uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-4">Ingredient</div>
                <div className="col-span-2">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-1">Wastage %</div>
                <div className="col-span-2 text-right">Cost</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              <div className="divide-y divide-[#27272a]">
                {lines.map((line, idx) => {
                  const cost = lineCosts[idx];
                  return (
                    <div key={line._key} className="grid grid-cols-12 gap-2 px-2 py-2 items-center" data-testid={`recipe-editor-row-${idx}`}>
                      <div className="col-span-1 text-xs text-zinc-500 font-mono">{idx + 1}</div>
                      <div className="col-span-4">
                        <SmartIngredientInput
                          ingredients={ingredients}
                          value={line.ingredient_id}
                          onChange={(id) => updateLine(idx, { ingredient_id: id })}
                          testIdPrefix={`recipe-editor-ing-${idx}`}
                          onEnter={() => qtyRefs.current[idx]?.focus()}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          ref={(el) => qtyRefs.current[idx] = el}
                          type="number" step="0.01" min="0" value={line.qty}
                          onChange={(e) => updateLine(idx, { qty: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); focusNextQty(idx); }
                            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd') { e.preventDefault(); dupLine(idx); }
                            if ((e.metaKey || e.ctrlKey) && (e.key === 'Delete' || e.key === 'Backspace')) { e.preventDefault(); removeLine(idx); }
                          }}
                          data-testid={`recipe-editor-qty-${idx}`}
                          className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div className="col-span-1">
                        <select
                          value={line.base_unit}
                          onChange={(e) => updateLine(idx, { base_unit: e.target.value })}
                          data-testid={`recipe-editor-unit-${idx}`}
                          className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                        >
                          {ACCEPTED_UNITS_FOR_UI.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="col-span-1">
                        <input
                          type="number" step="0.1" min="0" value={line.wastage_percent}
                          placeholder="0"
                          onChange={(e) => updateLine(idx, { wastage_percent: e.target.value })}
                          data-testid={`recipe-editor-wastage-${idx}`}
                          className="w-full px-2 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div className={`col-span-2 text-right text-sm font-mono ${cost.mismatch ? 'text-red-400' : cost.missing ? 'text-zinc-600' : 'text-emerald-400'}`} data-testid={`recipe-editor-line-cost-${idx}`}>
                        {cost.mismatch ? '—' : (cost.cost ? formatCurrency(cost.cost) : '—')}
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <button type="button" onClick={() => dupLine(idx)} title="Duplicate" data-testid={`recipe-editor-dup-${idx}`} className="p-1 text-zinc-500 hover:text-zinc-200"><Copy className="w-3.5 h-3.5" /></button>
                        {lines.length > 1 && (
                          <button type="button" onClick={() => removeLine(idx)} title="Remove" data-testid={`recipe-editor-remove-${idx}`} className="p-1 text-red-500 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live summary */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 bg-emerald-500/5 border border-emerald-500/20 rounded">
                <p className="text-xs text-zinc-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Recipe Cost</p>
                <p className="text-lg font-mono font-bold" data-testid="recipe-editor-batch-cost">{formatCurrency(totalCost)}</p>
              </div>
              <div className="p-2 bg-emerald-500/5 border border-emerald-500/20 rounded">
                <p className="text-xs text-zinc-500">Per {yieldUnit}</p>
                <p className="text-lg font-mono font-bold text-emerald-400" data-testid="recipe-editor-cost-per-portion">{formatCurrency(costPerYield)}</p>
              </div>
              <div className={`p-2 border rounded ${missingCount ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-[#09090b] border-[#27272a]'}`}>
                <p className="text-xs text-zinc-500">Missing costs</p>
                <p className={`text-lg font-mono font-bold ${missingCount ? 'text-yellow-400' : 'text-zinc-300'}`} data-testid="recipe-editor-missing-costs">{missingCount}</p>
              </div>
              <div className={`p-2 border rounded ${(invalidUnits + mismatchCount) ? 'bg-red-500/5 border-red-500/30' : 'bg-[#09090b] border-[#27272a]'}`}>
                <p className="text-xs text-zinc-500">Unit issues</p>
                <p className={`text-lg font-mono font-bold ${(invalidUnits + mismatchCount) ? 'text-red-400' : 'text-zinc-300'}`} data-testid="recipe-editor-unit-issues">{invalidUnits + mismatchCount}</p>
              </div>
            </div>

            {hasUnresolvedRows && (
              <p className="mt-2 text-xs text-red-400 flex items-center gap-1" data-testid="recipe-editor-unresolved-warning">
                <AlertCircle className="w-3 h-3" /> One or more rows have an unresolved ingredient. Please select from the master list before saving.
              </p>
            )}
          </div>

          {/* Optional notes + edit reason */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1">Notes</label>
              <input
                type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Slow simmer 45 min"
                data-testid="recipe-editor-notes"
                className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
              />
            </div>
            {mode === 'edit' && (
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1">Reason for edit (optional, audited)</label>
                <input
                  type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Recipe revision per chef"
                  data-testid="recipe-editor-reason"
                  className="w-full px-3 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md text-sm focus:border-emerald-500 outline-none"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => onClose?.()} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-2 px-4 rounded-md transition-colors" data-testid="recipe-editor-cancel-btn">Cancel</button>
            <button
              type="button" onClick={handleSave}
              disabled={saving || hasUnresolvedRows || invalidUnits > 0 || !name.trim() || Number(yieldQty) <= 0 || yieldUnitInvalid}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="recipe-editor-save-btn"
            >{saving ? 'Saving…' : (mode === 'edit' ? 'Save Changes' : 'Create Recipe')}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecipeEditorDialog;
